// A transcript laid out as an article: what YouTube captions and podcast
// transcripts both become. Pure functions only, shared by the server (which
// builds the stored HTML) and the web reader (which formats times).

import type { TocEntry } from './types.ts';

export interface Cue {
  /** Seconds from the start; null for a transcript that has no timings. */
  start: number | null;
  text: string;
  /** Who is speaking, when the transcript says. */
  speaker?: string;
}

export interface Chapter {
  start: number;
  title: string;
}

export interface Transcript {
  content_html: string;
  content_md: string;
  word_count: number;
  excerpt: string | null;
  toc: TocEntry[];
}

const ENTITIES: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ' };

/** Decode the character references XML and HTML text carries. */
export function decodeEntities(text: string): string {
  return text.replace(/&(#x[0-9a-f]+|#\d+|\w+);/gi, (whole, name: string) => {
    if (name[0] === '#') {
      const code = name[1] === 'x' || name[1] === 'X' ? parseInt(name.slice(2), 16) : parseInt(name.slice(1), 10);
      return Number.isFinite(code) && code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : whole;
    }
    return ENTITIES[name.toLowerCase()] ?? whole;
  });
}

/** Markup and references out, whitespace collapsed: a line's plain text. */
export function plainText(markup: string): string {
  return decodeEntities(markup.replace(/<[^>]*>/g, ' ')).replace(/\s+/g, ' ').trim();
}

export function formatTime(total: number): string {
  const s = Math.max(0, Math.floor(total));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return h ? `${h}:${pad(m)}:${pad(s % 60)}` : `${m}:${pad(s % 60)}`;
}

function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}

/** A paragraph runs on until a chapter starts, the speaker changes, a pause,
 *  or it has gone on long enough and a sentence ends — captions have lines,
 *  not paragraphs. */
const PARAGRAPH_SECONDS = 45;
const PARAGRAPH_MAX_SECONDS = 90;
/** Captions carry start times only; this long from one line's start to the
 *  next's is a pause, a line itself rarely lasting more than five seconds. */
const PAUSE_SECONDS = 8;

/**
 * Lay a transcript out as an article: chapters as headings, lines gathered
 * into paragraphs, a speaker's name opening each of their turns. Every timed
 * line keeps its start (`data-t`, in seconds), which is how the reader seeks
 * the video or audio from the text and follows it along. A line without a
 * time is a paragraph of its own. The markdown is the plain text, for
 * search, export and read-aloud.
 */
export function buildTranscript(cues: Cue[], chapters: Chapter[] = []): Transcript {
  const html: string[] = [];
  const md: string[] = [];
  const toc: TocEntry[] = [];
  let paragraph: Cue[] = [];
  let chapter = 0;
  let words = 0;
  let lastSpeaker: string | undefined;

  const flush = (): void => {
    if (!paragraph.length) return;
    const first = paragraph[0]!;
    const time = (cue: Cue): string => (cue.start === null ? '' : ` data-t="${cue.start}"`);
    const speaker = first.speaker && first.speaker !== lastSpeaker ? first.speaker : null;
    lastSpeaker = first.speaker ?? lastSpeaker;
    const lines = paragraph.map((cue) => `<span${time(cue)}>${escapeHtml(cue.text)}</span>`);
    const label = speaker ? `<strong class="speaker">${escapeHtml(speaker)}</strong> ` : '';
    html.push(`<p${time(first)}>${label}${lines.join(' ')}</p>`);
    const text = paragraph.map((cue) => cue.text).join(' ');
    md.push(speaker ? `**${speaker}:** ${text}` : text);
    paragraph = [];
  };

  for (const cue of cues) {
    while (cue.start !== null && chapter < chapters.length && chapters[chapter]!.start <= cue.start) {
      flush();
      const { start, title } = chapters[chapter]!;
      const id = `tr-chapter-${chapter + 1}`;
      html.push(`<h2 id="${id}" data-t="${start}">${escapeHtml(title)}</h2>`);
      md.push(`## ${title}`);
      toc.push({ title, href: id, depth: 0 });
      chapter++;
    }

    const previous = paragraph[paragraph.length - 1];
    if (previous) {
      if (cue.start === null || previous.start === null || cue.speaker !== previous.speaker) {
        flush();
      } else {
        const length = cue.start - paragraph[0]!.start!;
        const sentenceEnded = /[.!?…]["')\]]?$/.test(previous.text);
        if (cue.start - previous.start >= PAUSE_SECONDS
          || (length >= PARAGRAPH_SECONDS && sentenceEnded)
          || length >= PARAGRAPH_MAX_SECONDS) {
          flush();
        }
      }
    }
    paragraph.push(cue);
    words += cue.text.split(/\s+/).filter(Boolean).length;
  }
  flush();

  const text = md.filter((block) => !block.startsWith('## ')).join(' ').replace(/\*\*([^*]+):\*\* /g, '');
  return {
    content_html: html.join('\n'),
    content_md: md.join('\n\n'),
    word_count: words,
    excerpt: text ? (text.length > 280 ? `${text.slice(0, 277).trimEnd()}…` : text) : null,
    toc,
  };
}
